import { ref } from 'vue'
import { getUserFriend } from '@/shared/services/user.service'
import { getAuthStoreSafe } from '@/shared/utils/piniaSafe.util'
import socket from '@/shared/services/webSocket'
import { useChatStore } from '@/domains/chat/store/chatStore'

// Global state for friends list
const friendsList = ref([])
const isLoading = ref(false)
const isLoaded = ref(false)
const lastErrorAt = ref(0)
let currentRequest = null;
let listenersInitialized = false
const ERROR_COOLDOWN_MS = 30_000
const REFRESH_ON_CONNECT_DEBOUNCE_MS = 5_000
let lastRefreshOnConnectAt = 0
let statusChangeHandler = null
let onlineUsersHandler = null
let connectionSuccessHandler = null

function getChatStore() {
  try {
    return useChatStore()
  } catch {
    return null
  }
}

function isRealtimeSessionBlocked() {
  const auth = getAuthStoreSafe()
  return !auth || auth.isLoggedOut || auth.shouldPreventApiCall
}

const isOnline = (user) => user?.isLoggedIn === true

const filterActiveFriends = (list = []) => {
  return list.filter((friend) => {
    const friendData = friend.friend || friend
    return isOnline(friendData)
  })
}

const updateFriendStatusInList = (targetUserId, status) => {
  let updated = false

  friendsList.value = friendsList.value.map((friend) => {
    const friendData = friend.friend || friend
    const friendId = friendData.id || friendData._id

    if (friendId === targetUserId) {
      updated = true
      const updatedFriend = { ...friendData, isLoggedIn: status }
      return friend.friend
        ? { ...friend, friend: updatedFriend }
        : { ...friend, isLoggedIn: status }
    }
    return friend
  })

  return updated
}

const applyOnlineUsersSnapshot = (onlineUserIds = []) => {
  if (!Array.isArray(onlineUserIds)) return

  const onlineIdSet = new Set(onlineUserIds.map(String))

  friendsList.value = friendsList.value.map((friend) => {
    const friendData = friend.friend || friend
    const friendId = friendData.id || friendData._id
    const isLoggedIn = onlineIdSet.has(String(friendId))
    const updatedFriend = { ...friendData, isLoggedIn }

    return friend.friend
      ? { ...friend, friend: updatedFriend }
      : updatedFriend
  })
}

export function useFriends() {
  // Load friends from API
  const loadFriends = async (page = 1, limit = 100, forceRefetch = false, activeOnly = false) => {
    // ⚠️ Prevent API call if user has logged out
    const authStore = getAuthStoreSafe();
    if (!authStore || authStore.shouldPreventApiCall) {
      return [];
    }

    if (forceRefetch) {
      isLoaded.value = false
      lastErrorAt.value = 0
    }

    // Skip refetch during cooldown window after an error to avoid thundering herd
    const now = Date.now();
    if (!isLoaded.value && lastErrorAt.value && (now - lastErrorAt.value) < ERROR_COOLDOWN_MS) {
      return friendsList.value;
    }

    if (isLoaded.value) return friendsList.value

    // Return existing request if one is in progress
    if (currentRequest) return currentRequest;

    isLoading.value = true

    currentRequest = (async () => {
      const result = await getUserFriend(page, limit)
      if (result.success) {
        const data = activeOnly ? filterActiveFriends(result.data) : result.data
        friendsList.value = data
        isLoaded.value = true
        lastErrorAt.value = 0
        return data
      }

      lastErrorAt.value = Date.now()
      return []
    })().finally(() => {
      isLoading.value = false
      currentRequest = null
    });

    return currentRequest
  }

  // Find friend by username
  const findFriendByUsername = (username) => {
    return friendsList.value.find(friend => {
      const friendData = friend.friend || friend
      return friendData.username === username
    })
  }

  // Find friend by name
  const findFriendByName = (name) => {
    return friendsList.value.find(friend => {
      const friendData = friend.friend || friend
      const fullName = `${friendData.firstName} ${friendData.lastName}`
      return fullName === name ||
        friendData.firstName === name ||
        friendData.lastName === name
    })
  }

  // Get friend ID by username
  const getFriendIdByUsername = (username) => {
    const friend = findFriendByUsername(username)
    if (friend) {
      return friend.friend?.id || friend.id
    }
    return null
  }

  // Refresh friends list
  const refreshFriends = async () => {
    isLoaded.value = false
    lastErrorAt.value = 0
    return await loadFriends(1, 100, true)
  }

  initFriendsSocketListeners()

  return {
    friendsList,
    isLoading,
    isLoaded,
    loadFriends,
    findFriendByUsername,
    findFriendByName,
    getFriendIdByUsername,
    refreshFriends,
    updateFriendStatusInList,
    initFriendsSocketListeners,
    filterActiveFriends,
  }
}

function initFriendsSocketListeners() {
  if (listenersInitialized) return
  listenersInitialized = true

  const statusEvents = [
    'userStatusChanged',
    'friendStatusChanged',
    'userOnline',
    'userOffline',
    'userLoggedOut',
    'friendLoggedOut',
    'friendLoggedIn',
  ]

  statusChangeHandler = (payload) => {
    if (isRealtimeSessionBlocked()) return

    const targetId = payload?.userId || payload?.user?.id || payload?.id
    const newStatus = payload?.isLoggedIn ?? payload?.user?.isLoggedIn

    if (targetId == null) return
    if (newStatus === undefined) return

    const updated = updateFriendStatusInList(targetId, newStatus)
    getChatStore()?.updateUserStatusInConversations(targetId, newStatus)

    if (!updated && isLoaded.value) {
      refreshFriendsFromModule()
    }
  }

  onlineUsersHandler = (onlineUserIds) => {
    if (isRealtimeSessionBlocked()) return

    applyOnlineUsersSnapshot(onlineUserIds)

    friendsList.value.forEach((friend) => {
      const friendData = friend.friend || friend
      const friendId = friendData.id || friendData._id
      if (friendId == null) return
      getChatStore()?.updateUserStatusInConversations(friendId, friendData.isLoggedIn === true)
    })
  }

  connectionSuccessHandler = () => {
    if (isRealtimeSessionBlocked()) return

    const now = Date.now()
    if (now - lastRefreshOnConnectAt < REFRESH_ON_CONNECT_DEBOUNCE_MS) return
    lastRefreshOnConnectAt = now
    refreshFriendsFromModule()
  }

  statusEvents.forEach((event) => socket.on(event, statusChangeHandler))
  socket.on('onlineUsers', onlineUsersHandler)
  socket.on('connectionSuccess', connectionSuccessHandler)
}

async function refreshFriendsFromModule() {
  if (isRealtimeSessionBlocked()) return []

  isLoaded.value = false
  lastErrorAt.value = 0

  const result = await getUserFriend(1, 100)
  if (result.success) {
    friendsList.value = result.data
    isLoaded.value = true
    return result.data
  }

  lastErrorAt.value = Date.now()
  return []
}

/** Re-attach presence listeners after session logout teardown. */
export function ensureFriendsSocketListeners() {
  initFriendsSocketListeners()
}

/** Clear cached friends after session logout. */
export function resetFriendsSessionState() {
  resetFriendsSocketListeners();
  friendsList.value = []
  isLoaded.value = false
  lastErrorAt.value = 0
  currentRequest = null
}

function resetFriendsSocketListeners() {
  if (!listenersInitialized) return

  const statusEvents = [
    'userStatusChanged',
    'friendStatusChanged',
    'userOnline',
    'userOffline',
    'userLoggedOut',
    'friendLoggedOut',
    'friendLoggedIn',
  ]

  if (statusChangeHandler) {
    statusEvents.forEach((event) => socket.off(event, statusChangeHandler))
  }
  if (onlineUsersHandler) {
    socket.off('onlineUsers', onlineUsersHandler)
  }
  if (connectionSuccessHandler) {
    socket.off('connectionSuccess', connectionSuccessHandler)
  }

  listenersInitialized = false
  statusChangeHandler = null
  onlineUsersHandler = null
  connectionSuccessHandler = null
}
